<template>
  <div id="tags-view-container" class="tags-view-container">
    <div class="tags-view-wrapper">
      <router-link
        v-for="tag in views"
        ref="tag"
        :key="tag.path"
        :class="isActive(tag) ? 'active' : ''"
        :to="{ path: tag.path, query: tag.query, fullPath: tag.fullPath }"
        tag="span"
        class="tags-view-item"
        @click.middle="closeSelectedTag(tag)"
        @contextmenu.prevent="openMenu(tag, $event)"
      >
        {{ tag.meta.title }}
        <span class="el-icon-close" @click.prevent.stop="closeSelectedTag(tag)" />
      </router-link>
    </div>
    <ul v-show="visible" :style="{ left: left + 'px', top: top + 'px' }" class="contextmenu">
      <li @click="refreshSelectedTag(selectedTag)">刷新</li>
      <li @click="closeSelectedTag(selectedTag)">关闭</li>
      <li @click="closeOthersTags">关闭其他</li>
      <li @click="closeAllTags()">关闭所有</li>
    </ul>
  </div>
</template>

<script lang="ts">
import { ITagsView } from '@/types/app';
import { defineComponent } from 'vue';
import { tagsView } from '@/store';
import utils from '@/utils';

interface IState {
  visible: boolean;
  top: number;
  left: number;
  selectedTag: ITagsView;
}

export default defineComponent({
  name: 'TagsView',
  data() {
    return {
      visible: false,
      top: 0,
      left: 0,
      selectedTag: {} as ITagsView,
    } as IState;
  },
  computed: {
    views() {
      return tagsView.views;
    },
  },
  watch: {
    $route() {
      this.addTags();
      this.moveToCurrentTag();
    },

    visible(value) {
      if (value) {
        document.body.addEventListener('click', this.closeMenu);
      } else {
        document.body.removeEventListener('click', this.closeMenu);
      }
    },
  },
  mounted() {
    this.addTags();
  },
  methods: {
    isActive(route: ITagsView): boolean {
      return utils.trim(route.path, '/') === utils.trim(this.$route.path, '/');
    },

    addTags() {
      const { name } = this.$route;
      if (name) {
        tagsView.addView(this.$route);
      }
      return false;
    },

    moveToCurrentTag() {
      this.$nextTick(() => {
        for (const tag of this.views) {
          if (tag.path === this.$route.path) {
            // this.$refs.scrollPane.moveToTarget(tag)
            // when query is different then update

            if (tag.fullPath !== this.$route.fullPath) {
              tagsView.updateView(this.$route);
            }
            break;
          }
        }
      });
    },

    refreshSelectedTag(view: ITagsView) {
      tagsView.delView(view).then(() => {
        const { fullPath } = view;
        this.$nextTick(() => {
          this.$router
            .replace({
              path: `/redirect${fullPath}`,
            })
            .catch((err: Error) => {
              console.warn(err);
            });
        });
      });
    },

    closeSelectedTag(view: ITagsView) {
      tagsView.delView(view).then((views) => {
        if (this.isActive(view)) {
          this.toLastView(views);
        }
      });
    },

    closeOthersTags() {
      if (this.selectedTag.fullPath !== this.$route.path && this.selectedTag.fullPath !== undefined) {
        this.$router.push(this.selectedTag.fullPath).catch((err: Error) => {
          console.warn(err);
        });
      }
      tagsView.delOthersViews(this.selectedTag);
      this.moveToCurrentTag();
    },

    closeAllTags() {
      tagsView.delAllViews();
      this.toLastView(tagsView.views);
    },

    toLastView(views: ITagsView[]) {
      if (!views) return;
      const latestView = views.slice(-1)[0];
      if (latestView) {
        this.$router.push(latestView.fullPath || '/');
      } else {
        this.$router.push('/');
      }
    },

    openMenu(tag: ITagsView, e: MouseEvent) {
      const menuMinWidth = 105;
      const offsetLeft = this.$el.getBoundingClientRect().left; // container margin left
      const { offsetWidth } = this.$el; // container width
      const maxLeft = offsetWidth - menuMinWidth; // left boundary
      const left = e.clientX - offsetLeft + 15; // 15: margin right

      if (left > maxLeft) {
        this.left = maxLeft;
      } else {
        this.left = left;
      }

      this.top = e.clientY - 45;
      this.visible = true;
      this.selectedTag = tag;
    },

    closeMenu() {
      this.visible = false;
    },
  },
});
</script>

<style lang="scss" scoped>
.tags-view-container {
  height: 36px;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid #d8dce5;
  .tags-view-wrapper {
    .tags-view-item {
      display: inline-block;
      position: relative;
      cursor: pointer;
      text-decoration: none;
      height: 26px;
      line-height: 26px;
      border: 1px solid #d8dce5;
      color: #495060;
      background: #fff;
      padding: 0 8px;
      font-size: 12px;
      margin-left: 5px;
      margin-top: 4px;
      &:first-of-type {
        margin-left: 15px;
      }
      &:last-of-type {
        margin-right: 15px;
      }
      &.active {
        background-color: #409eff;
        color: #fff;
        border-color: #409eff;
        &::before {
          content: '';
          background: #fff;
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          position: relative;
          margin-right: 2px;
        }
      }
    }
  }
  .contextmenu {
    margin: 0;
    background: #fff;
    z-index: 3000;
    position: absolute;
    list-style-type: none;
    padding: 5px 0;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 400;
    color: #333;
    box-shadow: 2px 2px 3px 0 rgba(0, 0, 0, 0.3);
    li {
      margin: 0;
      padding: 7px 16px;
      cursor: pointer;
      &:hover {
        background: #eee;
      }
    }
  }
}
</style>

<style lang="scss">
//reset element css of el-icon-close
.tags-view-wrapper {
  .tags-view-item {
    .el-icon-close {
      width: 16px;
      height: 16px;
      vertical-align: 2px;
      border-radius: 50%;
      text-align: center;
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      transform-origin: 100% 50%;
      &:before {
        transform: scale(0.6);
        display: inline-block;
        vertical-align: -3px;
      }
      &:hover {
        background-color: #b4bccc;
        color: #fff;
      }
    }
  }
}
</style>
